// AppErrorBoundary.tsx
import React, { ReactNode } from "react";
import { useErrorStore } from "../store/errorStore";
import { trackAppError } from "../analytics/analyticsUtil";
import ErrorScreen from "./ErrorScreen";

interface AppErrorBoundaryProps {
    children: ReactNode;
}

interface AppErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

function FallbackComponent({ error }: { error: Error | null }) {
    return <ErrorScreen/>;
}

export class AppErrorBoundary extends React.Component<
    AppErrorBoundaryProps,
    AppErrorBoundaryState
> {
    constructor(props: AppErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        //console.error("React ErrorBoundary caught:", error, info);
        // ✅ send to monitoring/logging service
        trackAppError({
            error: error.message,
            stack: error.stack!,
            componentStack: info?.componentStack!,
            isFatal: true,
            source: "AppErrorBoundary",
        });
        const { setError } = useErrorStore.getState();
        setError(error); // store error globally if you want
    }

    render() {
        if (this.state.hasError) {
            return <FallbackComponent error={this.state.error} />;
        }
        return this.props.children;
    }
}

